import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAuthTracking } from '../../hooks/useAnalytics';
import { logger } from '../../utils/logger';
import PageHeader from '../common/PageHeader';
import { GenderOption } from '../../types/user';
import { useAnalytics } from '../../hooks/useAnalytics';

/**
 * SignUp component for user registration
 */
const SignUp: React.FC = () => {
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [gender, setGender] = useState<GenderOption | ''>('');
    const [genderOther, setGenderOther] = useState<string>('');
    const [birthDate, setBirthDate] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [registrationComplete, setRegistrationComplete] = useState<boolean>(false);
    const [registeredEmail, setRegisteredEmail] = useState<string>('');

    const { signUp } = useAuth();
    const { trackSignUpEvent } = useAuthTracking();
    const { trackPageView, trackCustomEvent } = useAnalytics();

    // Track page view when component mounts
    useEffect(() => {
        trackPageView({
            page_title: 'Sign Up',
            page_location: window.location.href,
            page_referrer: document.referrer
        });
        
        // Track signup page view
        trackCustomEvent('signup_page_viewed', {
            timestamp: Date.now()
        });
    }, [trackPageView, trackCustomEvent]);

    // Calculate max date (13 years ago from today)
    const getMaxDate = (): string => {
        const date = new Date();
        date.setFullYear(date.getFullYear() - 13);
        return date.toISOString().split('T')[0];
    };

    // Gender options array
    const genderOptions: GenderOption[] = [
        'Man',
        'Woman',
        'Agender',
        'Androgynous',
        'Bigender',
        'Gender Fluid',
        'Gender Nonconforming',
        'Genderqueer',
        'Gender questioning',
        'Intersex',
        'Non-binary',
        'Other',
        'Pangender',
        'Transfeminine',
        'Transmasculine',
        'Trans person',
        'Trans Man',
        'Trans Non-binary',
        'Trans Woman',
        'Two-Spirit',
    ];

    // Validate password strength
    const isPasswordStrong = (password: string): boolean => {
        // Password must be at least 6 characters
        return password.length >= 6;
    };

    // Handle sign up submission
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Reset states
        setError('');        // Validate form inputs
        if (!email || !password || !confirmPassword || !firstName || !birthDate || !gender) {
            const missingFields = [];
            if (!email) missingFields.push('email');
            if (!password) missingFields.push('password');
            if (!confirmPassword) missingFields.push('confirm_password');
            if (!firstName) missingFields.push('first_name');
            if (!birthDate) missingFields.push('birth_date');
            if (!gender) missingFields.push('gender');
            
            // Track validation error
            trackCustomEvent('signup_validation_error', {
                missing_fields: missingFields
            });
            
            setError('Please fill in all required fields');
            return;
        }

        if (password !== confirmPassword) {
            // Track validation error
            trackCustomEvent('signup_validation_error', {
                error_type: 'password_mismatch'
            });
            
            setError('Passwords do not match');
            return;
        }

        if (!isPasswordStrong(password)) {
            // Track validation error
            trackCustomEvent('signup_validation_error', {
                error_type: 'weak_password',
                password_length: password.length
            });
            
            setError('Password must be at least 6 characters');
            return;
        }

        // Validate age (must be at least 13 years old)
        const birthDateObj = new Date(birthDate);
        const today = new Date();
        const minAgeDate = new Date();
        minAgeDate.setFullYear(today.getFullYear() - 13);
        
        if (birthDateObj > minAgeDate) {
            // Track age validation failure
            trackCustomEvent('signup_validation_error', {
                error_type: 'underage'
            });
            
            setError('You must be at least 13 years old to create an account');
            return;
        }

        // If gender is "Other", validate that genderOther is filled
        if (gender === 'Other' && !genderOther.trim()) {
            // Track validation error
            trackCustomEvent('signup_validation_error', {
                error_type: 'missing_gender_other'
            });
            
            setError('Please specify your gender');
            return;
        }

        try {
            setLoading(true);

            // Track signup attempt with demographics (but no PII)
            trackCustomEvent('signup_attempt', {
                email_domain: email.split('@')[1],
                gender_category: gender === 'Other' ? 'other' : gender,
                age_range: calculateAgeRange(birthDate)
            });            // Create user with email and password
            await signUp(email, password, firstName, lastName, gender, gender === 'Other' ? genderOther : undefined, birthDate);

            // Track successful sign up
            trackSignUpEvent('email');
            trackCustomEvent('signup_success', {
                email_domain: email.split('@')[1],
                gender_category: gender === 'Other' ? 'other' : gender,
                age_range: calculateAgeRange(birthDate)
            });

            // Set registration complete state
            setRegisteredEmail(email);
            setRegistrationComplete(true);
            logger.info('✅ User account created successfully');            // Reset form
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setFirstName('');
            setLastName('');
            setGender('');
            setGenderOther('');
            setBirthDate('');
        } catch (err: any) {
            logger.error('❌ Sign up error', err);

            // Track signup error
            trackCustomEvent('signup_error', {
                error_code: err.code || 'unknown',
                error_message: err.message || 'Unknown error'
            });

            // Handle specific Firebase Auth errors
            if (err.code === 'auth/email-already-in-use') {
                setError('Email is already in use');
            } else if (err.code === 'auth/invalid-email') {
                setError('Invalid email format');
            } else if (err.code === 'auth/weak-password') {
                setError('Password is too weak. Please use a stronger password.');
            } else {
                setError('Failed to create account. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };
    
    // Calculate age range for analytics (without sending exact age)
    const calculateAgeRange = (birthDateStr: string): string => {
        const birthDate = new Date(birthDateStr);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        
        if (age < 18) return '13-17';
        if (age < 25) return '18-24';
        if (age < 35) return '25-34';
        if (age < 45) return '35-44';
        if (age < 55) return '45-54';
        return '55+';
    };

    return (
        <>
            <PageHeader
                title="Members Sign Up"
                subtitle="Join as a member into our community"
            />
            <div className="sign-up-container">
                {registrationComplete ? (
                    // Registration confirmation page
                    <div className="registration-confirmation">
                        <h2 className="text-center">Registration Complete!</h2>
                        <div className="auth-success">
                            <p>We've sent a verification email to <strong>{registeredEmail}</strong>.</p>
                            <p>Please check your inbox and click the verification link to activate your account.</p>
                        </div>
                        <div className="confirmation-actions">
                            <p>What's next?</p>
                            <ul>
                                <li>Check your email and click the verification link</li>
                                <li>The verification link will activate your account</li>
                                <li>Once verified, you can sign in to access member features</li>
                            </ul>
                            <button 
                                className="primary-button" 
                                onClick={() => {
                                    // Track navigation from completed registration to sign-in
                                    trackCustomEvent('post_signup_navigation', {
                                        destination: 'sign_in'
                                    });
                                    window.location.href = '/sign-in';
                                }}
                            >
                                Go to Sign In
                            </button>
                        </div>
                    </div>
                ) : (
                    // Registration form
                    <>
                        <h2 className="text-center">Create an Account</h2>

                        {error && <div className="auth-error">{error}</div>}                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="firstName">First Name *</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    disabled={loading}
                                    placeholder="Enter your first name"
                                    autoComplete="given-name"
                                />
                                <small className="form-hint">Your real name will be kept private (visible only to admins)</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="lastName">Last Name</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    disabled={loading}
                                    placeholder="Enter your last name (optional)"
                                    autoComplete="family-name"
                                />
                                <small className="form-hint">Optional - Your real name will be kept private</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address *</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={loading}
                                    placeholder="Enter your email"
                                    autoComplete="username email"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="birthDate">Date of Birth</label>
                                <input
                                    type="date"
                                    id="birthDate"
                                    value={birthDate}
                                    onChange={(e) => setBirthDate(e.target.value)}
                                    max={getMaxDate()}
                                    required
                                    disabled={loading}
                                />
                                <small className="form-hint">You must be at least 13 years old</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="gender">Gender</label>
                                <select
                                    id="gender"
                                    value={gender}
                                    onChange={(e) => {
                                        const selectedGender = e.target.value as GenderOption;
                                        setGender(selectedGender);
                                        
                                        // Track gender selection (without PII)
                                        if (selectedGender) {
                                            trackCustomEvent('signup_field_completed', {
                                                field_name: 'gender',
                                                field_value_category: selectedGender === 'Other' ? 'other' : selectedGender
                                            });
                                        }
                                    }}
                                    required
                                    disabled={loading}
                                    className="auth-select"
                                >
                                    <option value="" disabled>Select your gender</option>
                                    {genderOptions.map((option) => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>

                            {gender === 'Other' && (
                                <div className="form-group">
                                    <label htmlFor="genderOther">Please specify</label>
                                    <input
                                        type="text"
                                        id="genderOther"
                                        value={genderOther}
                                        onChange={(e) => setGenderOther(e.target.value)}
                                        placeholder="Specify your gender"
                                        disabled={loading}
                                        maxLength={50}
                                    />
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        
                                        // Track password strength without exposing the password
                                        if (e.target.value.length > 0) {
                                            const strength = e.target.value.length >= 10 ? 'strong' : 
                                                           (e.target.value.length >= 6 ? 'medium' : 'weak');
                                            
                                            trackCustomEvent('password_strength_change', {
                                                strength: strength,
                                                length_category: e.target.value.length < 6 ? 'too_short' : 
                                                               (e.target.value.length < 10 ? 'acceptable' : 'good')
                                            });
                                        }
                                    }}
                                    required
                                    disabled={loading}
                                    placeholder="Create a password"
                                    autoComplete="new-password"
                                />
                                <small className="form-hint">Password must be at least 6 characters</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirm-password">Confirm Password</label>
                                <input
                                    type="password"
                                    id="confirm-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    placeholder="Confirm your password"
                                    autoComplete="new-password"
                                />
                            </div>

                            <button
                                type="submit"
                                className="primary-button"
                                disabled={loading}
                            >
                                {loading ? 'Creating Account...' : 'Sign Up'}
                            </button>
                        </form>

                        <div className="auth-links">
                            <Link 
                                to="/sign-in"
                                onClick={() => trackCustomEvent('navigation_click', {
                                    source: 'signup_page',
                                    destination: 'signin'
                                })}
                            >
                                Already have an account? Sign In
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default SignUp;
